import { Reducer, useEffect, useReducer } from 'react';
import { useHistory } from 'react-router-dom';

import { Box, FlexContainer } from '../../../../shared/Typhography';

import { ColoringGridTable } from './Shared';

export type FilterReducerState = {
  artist: string | null;
  collection: string | null;
  aiGenerated: boolean | null;
  status: string | null;
  premiumTier?: number | null;
};

export type FilterReducerAction = {
  type: 'ARTIST' | 'COLLECTION' | 'AI_GENERATED' | 'UPDATE_FROM_URL' | 'STATUS' | 'PREMIUMTIER';
  value?: number | string | boolean | null;
};

const filterReducer: Reducer<FilterReducerState, FilterReducerAction> = (state, action) => {
  switch (action.type) {
    case 'COLLECTION':
      return {
        ...state,
        collection:
          typeof action.value === 'string' && action.value !== 'all' ? action.value : null,
      };
    case 'ARTIST':
      return {
        ...state,
        artist: typeof action.value === 'string' && action.value !== 'all' ? action.value : null,
      };
    case 'AI_GENERATED':
      return { ...state, aiGenerated: typeof action.value === 'boolean' ? action.value : null };
    case 'STATUS':
      return {
        ...state,
        status: typeof action.value === 'string' && action.value !== 'all' ? action.value : null,
      };
    case 'PREMIUMTIER':
      return {
        ...state,
        premiumTier: action.value !== null ? (action.value as number) : null,
      };
    case 'UPDATE_FROM_URL': {
      const queryParams = new URLSearchParams(window.location.search);
      return {
        artist: queryParams.get('artist'),
        collection: queryParams.get('category'),
        licence: queryStringToBoolean(queryParams.get('licence')),
        aiGenerated: queryStringToBoolean(queryParams.get('aiGenerated')),
        status: queryParams.get('status'),
      };
    }
  }
};

export const ColoringGridsScreen = () => {
  const history = useHistory();
  const queryParams = new URLSearchParams(window.location.search);

  const [filterState, filtersDispatch] = useReducer(filterReducer, {
    artist: queryParams.get('artist'),
    collection: queryParams.get('category'),
    aiGenerated: queryStringToBoolean(queryParams.get('aiGenerated')),
    status: queryParams.get('status'),
    premiumTier: queryParams.get('premiumTier')
      ? parseInt(queryParams.get('premiumTier') as string)
      : null,
  });

  const { collection, artist, aiGenerated, status, premiumTier } = filterState;

  useEffect(() => {
    // Build search url based on filter state.
    const searchParams = new URLSearchParams();
    artist && searchParams.append('artist', artist);
    collection && searchParams.append('category', collection);
    aiGenerated && searchParams.append('aiGenerated', aiGenerated.toString());
    status && searchParams.append('status', status);
    premiumTier && searchParams.append('premium', String(premiumTier));

    // Push new url to history, so it can be re-visited later or bookmarked.
    history.push(`/coloring-grids?${searchParams}`);
  }, [collection, artist, aiGenerated, status, premiumTier]);

  return (
    <Box margin="150px auto">
      <FlexContainer flexDirection="column" gap="2rem">
        <ColoringGridTable filterState={[filterState, filtersDispatch]} />
      </FlexContainer>
    </Box>
  );
};

/**
 * Simple function just to be used with this screen when boolean value needs to be
 * parsed from search parameter.
 * It expects a string with literal value "true" or "false" and converts it to its
 * boolean counterpart.
 *
 * If passed in string is neither of those, it will default to null.
 *
 * @param string
 */
function queryStringToBoolean(string: unknown) {
  if (string === 'true') return true;
  if (string === 'false') return false;

  return null;
}
